// FullStory - commented out: import { FullStory } from '@fullstory/browser';
import DOMPurify from 'dompurify';
import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Form } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import { useLocation } from 'react-router';
import { FavouriteTag } from '../../hooks/filters/interfaces/favourite-value.interface';
import { postSendEmailContactUs } from '../../services/post-send-email.service';
import { PATH_TAGS } from '../../static-resources/static-misc';
import './app-report-problem.modal.scss';
import { formatDate } from '../../utils/formatting/date-formatter.helper';
interface SelectOption {
  label: string;
  value: string;
}
const COMPONENT_MAP: Record<string, SelectOption[]> = {
  '/t/pea/c/rd': [{ value: 'PEA - Overview', label: 'PEA - Overview' }],
  '/t/pea/ca': [
    { value: 'PEA Date Overrides', label: 'PEA Date Overrides' },
    { value: 'PEA Product Overrides', label: 'PEA Product Overrides' },
    { value: 'PEA Volume Overrides', label: 'PEA Volume Overrides' },
    { value: 'PEA Merch Spend Overrides', label: 'PEA Merch Spend Overrides' },
    { value: 'PEA Review', label: 'PEA Review' },
  ],
  '/t/pea/dashboard': [
    {
      value: 'PEA Report - Retailer Overview',
      label: 'PEA Report - Retailer Overview',
    },
    { value: 'PEA Report - Share Impact', label: 'PEA Report - Share Impact' },
    {
      value: 'PEA Report - Base vs Incremental',
      label: 'PEA Report - Base vs Incremental',
    },
    {
      value: 'PEA Report - YoY Comparison',
      label: 'PEA Report - YoY Comparison',
    },
    { value: 'PEA Report - CBR', label: 'PEA Report - CBR' },
  ],
  '/t/pea/dashboard/event': [
    { value: 'PEA Report - Event Detail', label: 'PEA Report - Event Detail' },
  ],
  '/t/s': [
    {
      value: 'Price & Promo - Landing Page',
      label: 'Price & Promo - Landing Page',
    },
    {
      value: 'Price & Promo - Plan Page',
      label: 'Price & Promo - Plan Page',
    },
  ],
};
const MODULE_SUBJECT_MAP: Record<string, string> = {
  '/t/pea/c/rd': 'PEA - ',
  '/t/pea/ca': 'PEA - ',
  '/t/pea/dashboard': 'PEA - ',
  '/t/pea/dashboard/event': 'PEA - ',
  '/t/s': 'Price & Promo - ',
};
interface AppReportProblemModalProps {
  show: boolean;
  onClose?(show: boolean): void;
  pageId: string;
}

function generateUID() {
  const now = new Date();
  const yearMonthHours = parseInt(formatDate(now, 'YYMMDDHH'));
  const offset = now.getTimezoneOffset();
  // Adding timezone
  const time = offset * 100000000 + yearMonthHours;
  // First part is an alphanumeric, using year+month+hours
  const firstPart = time.toString(36).toUpperCase();
  // Last numbers are minutes+seconds+milliseconds
  const lastPart = formatDate(now, 'mmssSSS');
  const key = `${firstPart}${lastPart}`;
  return key;
}
/**
 */
export const AppReportProblemModal = ({
  show,
  onClose,
  pageId,
}: AppReportProblemModalProps) => {
  const [fsLink, setFsLink] = useState<string | undefined | null>();
  // FullStory - commented out
  // useEffect(() => {
  //   FullStory('observe', {
  //     type: 'start',
  //     callback: (op) => {
  //       const sessionUrl = FullStory('getSession');
  //       setFsLink(sessionUrl);
  //     },
  //   });
  // }, []);
  const form = useRef(null);
  const [validated, setValidated] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isError, setIsError] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string | undefined>();
  const [description, setDescription] = useState<string | undefined>();

  const tags: FavouriteTag[] | undefined = PATH_TAGS[pageId];
  const pageTitle = useMemo(() => {
    const title = tags ? (tags[0]?.title ?? '') : '';
    return title;
  }, [tags]);

  const options = useMemo(() => {
    const optns = COMPONENT_MAP[pageId];
    if (!optns) {
      return [
        {
          label: pageTitle,
          value: pageTitle,
        },
      ];
    }
    return optns;
  }, [pageId, pageTitle]);

  const location = useLocation();

  const link = useMemo(() => {
    const origin = window.location.origin;
    const baseUrl = location.pathname;
    const search = location.search;
    return `${origin}${baseUrl}${search}`;
  }, [location]);

  const issueId = useMemo(() => {
    return generateUID();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTitle, selectedValue]);

  const emailBody = useMemo(() => {
    if (!isValid || selectedValue === undefined || description === undefined) {
      return;
    }
    let fsInfo = '';
    if (fsLink) {
      fsInfo = `<p><a href="${DOMPurify.sanitize(fsLink)}" target="_blank" >FullStory Link</a></p>`;
    }
    const body = `<p><b>Page:</b> ${DOMPurify.sanitize(pageTitle)}</p>
<p><b>Report:</b> ${DOMPurify.sanitize(selectedValue)}</p>
<p><b>Issue ID:</b> #${issueId}</p>
<p><b>Problem Description:</b></p>
<p><pre>${DOMPurify.sanitize(description)}</pre></p>
<p><a href="${DOMPurify.sanitize(link)}" target="_blank" >Page Link</a></p>${fsInfo}`;
    return body;
  }, [description, isValid, link, pageTitle, selectedValue, fsLink, issueId]);

  const emailSubject = useMemo(() => {
    const subjectPrefix = MODULE_SUBJECT_MAP[pageId] ?? '';
    return `${subjectPrefix}Report a problem - ${issueId}`;
  }, [issueId, pageId]);

  const sendEmail = useCallback(async () => {
    if (!emailBody) {
      return;
    }
    setIsSending(true);
    setIsError(false);
    try {
      await postSendEmailContactUs(emailSubject, emailBody);
      setIsSubmitted(true);
    } catch {
      setIsError(true);
    }
    setIsSending(false);
  }, [emailBody, emailSubject]);

  const handleClose = useCallback(() => {
    onClose?.(false);
    setValidated(false);
    setDescription(undefined);
    setSelectedValue(undefined);
    setIsValid(false);
    setIsSubmitted(false);
    setIsError(false);
  }, [onClose]);

  const handleSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleBack = useCallback(() => {
    setIsSubmitted(false);
    setIsValid(false);
    setValidated(false);
  }, []);

  const handleSend = useCallback(() => {
    if (form.current) {
      const f: HTMLFormElement = form.current;
      if (f.checkValidity() === true) {
        setIsValid(true);
      }
      setValidated(true);
    }
  }, []);

  const handleConfirm = useCallback(async () => {
    if (form.current) {
      const f: HTMLFormElement = form.current;
      if (f.checkValidity() === true) {
        await sendEmail();
      }
    }
  }, [sendEmail]);

  const onChangeHandler = useCallback((e: any) => {
    const value: string = e?.target?.value;
    if (value) {
      setSelectedValue(value);
    }
  }, []);
  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      className="app-report-problem-modal"
      aria-labelledby="contained-modal-title-vcenter"
      centered={true}
    >
      <Modal.Header closeButton>
        <Modal.Title>Report a problem</Modal.Title>
      </Modal.Header>
      <Form ref={form} noValidate validated={validated} onSubmit={handleSubmit}>
        <Modal.Body className="">
          {isSending && <p>Sending email...</p>}
          {isSubmitted && <p>Thank you. The report was sent.</p>}
          {isError && (
            <p className="bg-warning">
              There was an error when sending the email report.
            </p>
          )}
          {!isSubmitted && (
            <>
              <div className={`container-fluid p-0 ${isValid ? 'd-none' : ''}`}>
                <div className="row gy-2 align-items-center">
                  <div className="col-lg-12">
                    <div className="row">
                      <div className="col-12 mb-3">
                        <p>Let our team know what’s going on.</p>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12">
                        <Form.Group className="mb-3">
                          <Form.Label>{pageTitle} *</Form.Label>
                          <Form.Select
                            value={selectedValue}
                            onChange={onChangeHandler}
                            required
                          >
                            <option
                              key={0}
                              value={''}
                              disabled={
                                selectedValue === undefined ? false : true
                              }
                            >
                              Select an option
                            </option>
                            {options?.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            Dashboard is Required
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                          <Form.Label>Description *</Form.Label>
                          <textarea
                            required
                            className="form-control"
                            id="description"
                            rows={3}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Describe the problem"
                            maxLength={512}
                          />
                          <Form.Control.Feedback type="invalid">
                            Description is Required
                          </Form.Control.Feedback>
                        </Form.Group>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className={`container-fluid ${isValid ? '' : 'd-none'}`}>
                <p>
                  The following information will be sent to the IRIS Support
                  team:{' '}
                </p>
                <hr />
                <p>
                  <b>Page: </b>
                  {pageTitle}
                </p>

                <p>
                  <b>Report:</b> {selectedValue}
                </p>
                <p>
                  <b>Issue ID:</b> #{issueId}
                </p>
                <p>
                  <b>Problem Description:</b>{' '}
                </p>
                <pre>{description} </pre>
                <hr />
                <p>Are you sure you want to continue?</p>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="container-fluid p-0">
            {!isSubmitted && !isValid && (
              <div className="row">
                <div className="col-lg-6">
                  <Button
                    disabled={isSending}
                    onClick={handleClose}
                    className="cancel-button d-flex justify-content-center align-items-center btn-default"
                  >
                    Cancel
                  </Button>
                </div>
                <div className="col-lg-6">
                  <Button
                    disabled={isSending}
                    type="button"
                    variant="primary"
                    className={`confirm-button d-flex justify-content-center align-items-center`}
                    onClick={() => handleSend()}
                  >
                    Send
                  </Button>
                </div>
              </div>
            )}

            {!isSubmitted && isValid && (
              <div className="row">
                <div className="col-lg-6">
                  <Button
                    disabled={isSending}
                    onClick={handleBack}
                    className="cancel-button d-flex justify-content-center align-items-center btn-default"
                  >
                    Back
                  </Button>
                </div>
                <div className="col-lg-6">
                  <Button
                    disabled={isSending}
                    type="button"
                    variant="primary"
                    className={`confirm-button d-flex justify-content-center align-items-center`}
                    onClick={() => handleConfirm()}
                  >
                    Confirm
                  </Button>
                </div>
              </div>
            )}
            {isSubmitted && (
              <div className="row">
                <div className="col-lg-6">
                  <Button
                    onClick={handleClose}
                    className="cancel-button d-flex justify-content-center align-items-center btn-default"
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
